import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight, FiStar } from "react-icons/fi";
import male1 from "../asset/team/male1.jpeg";
import male2 from "../asset/team/male2.jpeg";
import female from "../asset/team/female.jpeg";

const Testimonials = () => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0); // -1: left, 1: right
  const timerRef = useRef(null);

  const reviews = [
    {
      img: male1,
      name: "John Smith",
      company: "United Logistics",
      rating: 5,
      text: "Softkel transformed our manual operations with a custom warehouse tracking system that increased our tracking speed by 40%. Their team is efficient and deep-dives into complex problems.",
    },
    {
      img: female,
      name: "Emma Larson",
      company: "Hico Solutions",
      rating: 5,
      text: "The dealer app Softkel built solved our coordination lag between salespeople, coordinators and service vendors. It has been a true game-changer for our daily operations.",
    },
    {
      img: male2,
      name: "Robert Müller",
      company: "Gil Tech",
      rating: 5,
      text: "Their SAP customized implementation saved us hundreds of engineering hours. They are highly reliable, transparent, and write clean, maintainable systems.",
    },
  ];

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setIndex((prevIndex) => (prevIndex === 0 ? reviews.length - 1 : prevIndex - 1));
  }, [reviews.length]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setIndex((prevIndex) => (prevIndex === reviews.length - 1 ? 0 : prevIndex + 1));
  }, [reviews.length]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      handleNext();
    }, 6000);
  }, [handleNext]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [index, resetTimer]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, type: "spring", stiffness: 100, damping: 20 }
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      transition: { duration: 0.3 }
    })
  };

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative">
      {/* Background radial blurs */}
      <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full bg-blue-500/5 dark:bg-purple-500/5 blur-3xl" />

      <div className="max-w-4xl mx-auto px-6 text-center">
        {/* Title */}
        <div className="mb-16">
          <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
            {t('testimonialsLabel', 'Reviews')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
            {t('testimonialsTitle', 'What Our Clients Say')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-purple-500 dark:to-pink-500 mx-auto mt-4 rounded-full" />
        </div>

        {/* Carousel Frame */}
        <div className="relative min-h-[380px] sm:min-h-[300px] flex items-center justify-center">
          
          {/* Navigation Buttons */}
          <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between z-20 pointer-events-none px-0 sm:-px-8">
            <button
              onClick={handlePrev}
              className="pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full bg-white dark:bg-[#111111] hover:bg-slate-100 dark:hover:bg-neutral-800 border border-slate-200/50 dark:border-neutral-800/40 text-slate-700 dark:text-slate-300 shadow-md transition-all hover:scale-105 active:scale-95"
              aria-label="Previous Testimonial"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleNext}
              className="pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full bg-white dark:bg-[#111111] hover:bg-slate-100 dark:hover:bg-neutral-800 border border-slate-200/50 dark:border-neutral-800/40 text-slate-700 dark:text-slate-300 shadow-md transition-all hover:scale-105 active:scale-95"
              aria-label="Next Testimonial"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Swipeable Testimonial Card */}
          <div className="w-full max-w-2xl overflow-hidden py-6 px-4">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={index}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="flex flex-col items-center bg-white dark:bg-[#111111] border border-slate-200/50 dark:border-neutral-800/40 rounded-3xl p-6 sm:p-8 shadow-sm dark:shadow-2xl"
              >
                {/* Stars Rating */}
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(reviews[index].rating)].map((_, i) => (
                    <FiStar key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg italic leading-relaxed text-center mb-8">
                  "{reviews[index].text}"
                </p>

                {/* Profile Meta */}
                <div className="flex items-center gap-4">
                  <img
                    src={reviews[index].img}
                    alt={reviews[index].name}
                    className="w-12 h-12 rounded-full object-cover shadow-md border-2 border-blue-500/20 dark:border-purple-500/20"
                  />
                  <div className="text-left">
                    <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">
                      {reviews[index].name}
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-neutral-500">
                      {reviews[index].company}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Indicator dots */}
        <div className="flex justify-center gap-2 mt-4">
          {reviews.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setDirection(idx > index ? 1 : -1);
                setIndex(idx);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === idx 
                  ? "bg-blue-600 dark:bg-purple-500 w-6" 
                  : "bg-slate-300 dark:bg-neutral-800"
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
